/**
 * Sidebar Layout Component
 *
 * Provides a consistent layout for all application pages with:
 * - Collapsible sidebar navigation
 * - Header with notifications and user profile
 * - Main content area
 * - Mobile-responsive design
 */

import { renderIcon } from '../utils/icons';
import { createUserProfile } from './user-profile';
import { createNotificationsDropdown } from './notifications-dropdown';
import { getAuthState, selectIsAdmin, selectUser } from '../services/auth-store';

/**
 * Navigation link configuration
 */
export interface SidebarNavLink {
  href: string;
  label: string;
  icon: string;
  /** Only show for specific roles */
  roles?: ('USER' | 'ASSISTANT' | 'ADMIN')[];
  /** Only show for admin users */
  adminOnly?: boolean;
  /** Separator before this link */
  separator?: boolean;
  /** Badge count to show on the link */
  badge?: number;
  /** ID for dynamic badge updates */
  badgeId?: string;
  /** Section header label to display above this link */
  sectionLabel?: string;
}

/**
 * Sidebar layout options
 */
export interface SidebarLayoutOptions {
  /** Current page path for highlighting active link */
  currentPath: string;
  /** Page title for header */
  title?: string;
  /** Page subtitle */
  subtitle?: string;
  /** Show header with title */
  showHeader?: boolean;
  /** Custom navigation links (defaults to standard app navigation) */
  navLinks?: SidebarNavLink[];
  /** Callback when navigation link is clicked */
  onNavigate?: (href: string) => void;
}

/**
 * Default navigation links for the application
 */
export const defaultSidebarLinks: SidebarNavLink[] = [
  // Dashboard (no section)
  { href: '/dashboard', label: 'Dashboard', icon: 'layoutDashboard' },
  // Expenditure Requests section
  { href: '/requests/new', label: 'New Request', icon: 'plus', sectionLabel: 'EXPENDITURE REQUESTS' },
  { href: '/my-requests', label: 'My Requests', icon: 'fileText' },
  { href: '/approvals', label: 'My Approvals', icon: 'checkCircle' },
  // Expense Reports section
  { href: '/expense-reports', label: 'My Reports', icon: 'receipt', sectionLabel: 'EXPENSE REPORTS' },
  // Settings section
  { href: '/settings', label: 'Settings', icon: 'settings', sectionLabel: 'SETTINGS' },
  // Admin section - visible to admins only
  { href: '/admin/requests', label: 'All Requests', icon: 'users', adminOnly: true, sectionLabel: 'ADMIN' },
  { href: '/kanban', label: 'Kanban Board', icon: 'columns', adminOnly: true },
  { href: '/reports', label: 'Analytics', icon: 'pieChart', adminOnly: true },
  { href: '/admin/settings', label: 'Admin Settings', icon: 'shieldCheck', adminOnly: true },
  // Team Assistant section - visible to ASSISTANT and ADMIN roles only
  { href: '/expense-reports-dashboard', label: 'Expense Reports', icon: 'clipboardList', roles: ['ASSISTANT', 'ADMIN'], sectionLabel: 'TEAM ASSISTANT', badgeId: 'pending-expense-reports' },
  { href: '/travel-requests', label: 'Travel Requests', icon: 'briefcase', roles: ['ASSISTANT', 'ADMIN'] },
  { href: '/out-of-pocket', label: 'Out-of-Pocket', icon: 'receipt', roles: ['ASSISTANT', 'ADMIN'] },
];

/**
 * Local storage key for sidebar collapsed state
 */
const SIDEBAR_COLLAPSED_KEY = 'wyden-sidebar-collapsed';

/**
 * Get saved sidebar collapsed state
 */
function getSavedCollapsedState(): boolean {
  try {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  } catch {
    return false;
  }
}

/**
 * Save sidebar collapsed state
 */
function saveCollapsedState(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Create sidebar layout component
 */
export function createSidebarLayout(
  content: HTMLElement,
  options: SidebarLayoutOptions
): HTMLElement {
  const {
    currentPath,
    title,
    subtitle,
    showHeader = true,
    navLinks = defaultSidebarLinks,
    onNavigate,
  } = options;

  const authState = getAuthState();
  const user = selectUser(authState);
  const isAdmin = selectIsAdmin(authState);
  const userRole = user?.role;

  // Filter links based on user role
  const filteredLinks = navLinks.filter((link) => {
    if (link.adminOnly && !isAdmin) return false;
    if (link.roles && !link.roles.includes(userRole as 'USER' | 'ASSISTANT' | 'ADMIN')) return false;
    return true;
  });

  let isCollapsed = getSavedCollapsedState();
  let isMobileOpen = false;

  // Create main container
  const container = document.createElement('div');
  container.className = 'min-h-screen flex bg-background';

  // Create sidebar
  const sidebar = document.createElement('aside');
  sidebar.id = 'app-sidebar';
  sidebar.className = `
    fixed inset-y-0 left-0 z-50 flex flex-col
    bg-card/95 backdrop-blur-xl border-r border-border
    transition-transform duration-300 ease-in-out
    md:transition-all
    ${isCollapsed ? 'w-16 md:w-16' : 'w-64 md:w-64'}
    -translate-x-full md:translate-x-0
  `.replace(/\s+/g, ' ').trim();

  // Sidebar header with brand
  const sidebarHeader = document.createElement('div');
  sidebarHeader.className = 'flex items-center h-16 px-4 border-b border-border shrink-0';

  const brandLink = document.createElement('a');
  brandLink.href = '/dashboard';
  brandLink.className = 'flex items-center gap-3 hover:opacity-80 transition-opacity overflow-hidden flex-1';
  brandLink.innerHTML = `
    <div class="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
      ${renderIcon('briefcase', { size: 'sm', className: 'text-primary' }).outerHTML}
    </div>
    <span class="font-semibold text-foreground whitespace-nowrap sidebar-label ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100'}">Wyden</span>
  `;
  brandLink.addEventListener('click', (e) => {
    e.preventDefault();
    closeMobileSidebar();
    navigateTo('/dashboard');
  });
  sidebarHeader.appendChild(brandLink);

  // Mobile close button (visible only on mobile)
  const mobileCloseBtn = document.createElement('button');
  mobileCloseBtn.className = 'md:hidden flex ml-auto w-8 h-8 items-center justify-center rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground';
  mobileCloseBtn.setAttribute('aria-label', 'Close navigation menu');
  mobileCloseBtn.innerHTML = renderIcon('x', { size: 'md' }).outerHTML;
  mobileCloseBtn.addEventListener('click', closeMobileSidebar);
  sidebarHeader.appendChild(mobileCloseBtn);

  // Collapse button (desktop only)
  const collapseBtn = document.createElement('button');
  collapseBtn.className = `
    hidden md:flex ml-2 w-8 h-8 items-center justify-center
    rounded-lg hover:bg-muted/50 transition-colors text-muted-foreground
    ${isCollapsed ? 'rotate-180' : ''}
  `.replace(/\s+/g, ' ').trim();
  collapseBtn.setAttribute('aria-label', 'Toggle sidebar');
  collapseBtn.innerHTML = renderIcon('chevronLeft', { size: 'sm' }).outerHTML;
  collapseBtn.addEventListener('click', () => {
    isCollapsed = !isCollapsed;
    saveCollapsedState(isCollapsed);
    updateSidebarState();
  });
  sidebarHeader.appendChild(collapseBtn);

  sidebar.appendChild(sidebarHeader);

  // Navigation links
  const nav = document.createElement('nav');
  nav.className = 'flex-1 overflow-y-auto py-4 px-2';

  filteredLinks.forEach((link) => {
    // Add section label if present
    if (link.sectionLabel) {
      const sectionHeader = document.createElement('div');
      sectionHeader.className = `px-3 pt-4 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider sidebar-label ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`;
      sectionHeader.textContent = link.sectionLabel;
      nav.appendChild(sectionHeader);
    }

    // Add separator if present (deprecated, but keep for backward compatibility)
    if (link.separator) {
      const separator = document.createElement('div');
      separator.className = 'my-2 mx-2 border-t border-border';
      nav.appendChild(separator);
    }

    const isActive = currentPath === link.href ||
      (link.href !== '/' && currentPath.startsWith(link.href + '/'));

    const navLink = document.createElement('a');
    navLink.href = link.href;
    navLink.className = `
      flex items-center gap-3 px-3 py-2.5 mb-1 rounded-lg
      transition-colors group relative
      ${isActive
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}
    `.replace(/\s+/g, ' ').trim();
    // Build badge HTML if badgeId or badge is present
    const badgeHtml = link.badgeId
      ? `<span id="sidebar-badge-${link.badgeId}" class="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-warning/20 text-warning sidebar-label hidden"></span>`
      : link.badge && link.badge > 0
        ? `<span class="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-warning/20 text-warning sidebar-label ${isCollapsed ? 'hidden' : ''}">${link.badge}</span>`
        : '';

    navLink.innerHTML = `
      <span class="shrink-0">${renderIcon(link.icon as Parameters<typeof renderIcon>[0], { size: 'md' }).outerHTML}</span>
      <span class="whitespace-nowrap sidebar-label ${isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}">${link.label}</span>
      ${badgeHtml}
      ${isCollapsed ? `
        <span class="absolute left-full ml-2 px-2 py-1 bg-popover text-popover-foreground text-sm rounded-md shadow-lg
          opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity whitespace-nowrap z-50">
          ${link.label}
        </span>
      ` : ''}
    `;
    navLink.addEventListener('click', (e) => {
      e.preventDefault();
      closeMobileSidebar();
      navigateTo(link.href);
    });
    nav.appendChild(navLink);
  });

  sidebar.appendChild(nav);

  // Mobile close button (overlay)
  const mobileOverlay = document.createElement('div');
  mobileOverlay.id = 'sidebar-overlay';
  mobileOverlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-40 opacity-0 invisible transition-all duration-300 md:hidden';
  mobileOverlay.addEventListener('click', closeMobileSidebar);

  // Main content area
  const mainWrapper = document.createElement('div');
  mainWrapper.className = `flex-1 flex flex-col min-h-screen transition-all duration-300 ease-in-out ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`;

  // Header
  const header = document.createElement('header');
  header.className = 'sticky top-0 z-30 glass border-b border-border';

  const headerContent = document.createElement('div');
  headerContent.className = 'flex items-center justify-between h-16 px-4 lg:px-6';

  // Left side: Mobile menu button + page title
  const headerLeft = document.createElement('div');
  headerLeft.className = 'flex items-center gap-4';

  // Mobile menu button
  const mobileMenuBtn = document.createElement('button');
  mobileMenuBtn.className = 'md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-muted/50 transition-colors';
  mobileMenuBtn.setAttribute('aria-label', 'Open navigation menu');
  mobileMenuBtn.innerHTML = renderIcon('menu', { size: 'md', className: 'text-foreground' }).outerHTML;
  mobileMenuBtn.addEventListener('click', openMobileSidebar);
  headerLeft.appendChild(mobileMenuBtn);

  // Page title
  if (showHeader && title) {
    const titleWrapper = document.createElement('div');
    titleWrapper.innerHTML = `
      <h1 class="text-xl font-semibold text-foreground">${title}</h1>
      ${subtitle ? `<p class="text-sm text-muted-foreground">${subtitle}</p>` : ''}
    `;
    headerLeft.appendChild(titleWrapper);
  }

  // Right side: Notifications + User profile
  const headerRight = document.createElement('div');
  headerRight.className = 'flex items-center gap-2';

  // Notifications dropdown
  const notificationsDropdown = createNotificationsDropdown({
    maxVisible: 10,
    autoStartPolling: true,
    onNotificationClick: (notification) => {
      if (notification.request_id) {
        navigateTo(`/requests/${notification.request_id}`);
      }
    },
  });
  headerRight.appendChild(notificationsDropdown);

  // User profile
  const userProfile = createUserProfile({
    showRole: true,
    showLogout: true,
    size: 'md',
  });
  headerRight.appendChild(userProfile);

  headerContent.appendChild(headerLeft);
  headerContent.appendChild(headerRight);
  header.appendChild(headerContent);

  // Main content
  const main = document.createElement('main');
  main.className = 'flex-1';
  main.appendChild(content);

  // Assemble main wrapper
  mainWrapper.appendChild(header);
  mainWrapper.appendChild(main);

  // Assemble container
  container.appendChild(mobileOverlay);
  container.appendChild(sidebar);
  container.appendChild(mainWrapper);

  /**
   * Navigate to a path
   */
  function navigateTo(href: string): void {
    if (onNavigate) {
      onNavigate(href);
    } else {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  }

  /**
   * Open mobile sidebar
   */
  function openMobileSidebar(): void {
    isMobileOpen = true;
    // Remove translate to show sidebar
    sidebar.classList.remove('-translate-x-full');
    // Show overlay with fade in
    mobileOverlay.classList.remove('invisible');
    mobileOverlay.classList.add('opacity-100');
    mobileOverlay.classList.remove('opacity-0');
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close mobile sidebar
   */
  function closeMobileSidebar(): void {
    isMobileOpen = false;
    // Add translate to hide sidebar
    sidebar.classList.add('-translate-x-full');
    // Hide overlay with fade out
    mobileOverlay.classList.remove('opacity-100');
    mobileOverlay.classList.add('opacity-0');
    // Wait for animation before hiding completely
    setTimeout(() => {
      if (!isMobileOpen) {
        mobileOverlay.classList.add('invisible');
      }
    }, 300);
    // Re-enable body scroll
    document.body.style.overflow = '';
  }

  /**
   * Update sidebar collapsed state
   */
  function updateSidebarState(): void {
    if (isCollapsed) {
      sidebar.classList.remove('w-64', 'md:w-64');
      sidebar.classList.add('w-16', 'md:w-16');
      mainWrapper.classList.remove('md:ml-64');
      mainWrapper.classList.add('md:ml-16');
      collapseBtn.classList.add('rotate-180');
    } else {
      sidebar.classList.remove('w-16', 'md:w-16');
      sidebar.classList.add('w-64', 'md:w-64');
      mainWrapper.classList.remove('md:ml-16');
      mainWrapper.classList.add('md:ml-64');
      collapseBtn.classList.remove('rotate-180');
    }

    // Update all sidebar labels
    sidebar.querySelectorAll('.sidebar-label').forEach((label) => {
      if (isCollapsed) {
        label.classList.add('opacity-0', 'w-0', 'overflow-hidden');
        label.classList.remove('opacity-100');
      } else {
        label.classList.remove('opacity-0', 'w-0', 'overflow-hidden');
        label.classList.add('opacity-100');
      }
    });

    // Update tooltips visibility
    sidebar.querySelectorAll('nav a').forEach((link) => {
      const tooltip = link.querySelector('span.absolute');
      if (tooltip) {
        if (isCollapsed) {
          tooltip.classList.remove('hidden');
        } else {
          tooltip.classList.add('hidden');
        }
      }
    });
  }

  return container;
}

/**
 * Higher-order function to wrap a view with sidebar layout
 */
export function withSidebarLayout(
  viewFn: () => HTMLElement,
  options: Omit<SidebarLayoutOptions, 'currentPath'> & { currentPath?: string }
): () => HTMLElement {
  return () => {
    const currentPath = options.currentPath || window.location.pathname;
    const content = viewFn();
    return createSidebarLayout(content, { ...options, currentPath });
  };
}

/**
 * Update a sidebar badge by its ID
 * @param badgeId - The ID suffix of the badge (e.g., 'pending-expense-reports')
 * @param count - The count to display (hidden if 0 or undefined)
 */
export function updateSidebarBadge(badgeId: string, count: number | undefined): void {
  const badge = document.getElementById(`sidebar-badge-${badgeId}`);
  if (badge) {
    if (count && count > 0) {
      badge.textContent = count.toString();
      badge.classList.remove('hidden');
    } else {
      badge.textContent = '';
      badge.classList.add('hidden');
    }
  }
}

export default createSidebarLayout;
